import React, { forwardRef, memo } from 'react';
import { makeStyles, createStyles, Hidden, IconButton } from '@material-ui/core';
import { useTranslation } from '../../app/i18n';
import { SidebarTypes } from './services/baseLayout.reducer';
import {
    useBaseLayoutState,
    useBaseLayoutCloseSide,
    useBaseLayoutOpenSide
} from './BaseLayoutProvider';
import clsx from 'clsx';
import ApsyIcon from 'src/assets/icons/ApsyIcon';
import Link from 'next/link';
// import { useAuth } from 'src/providers/auth/AuthProvider';
import MenuIcon from 'src/assets/icons/common/Menu';
import baseLayoutStyles from './styles/baseLayoutStyles';

import { CloseButton, NavButton, NavTypography } from './sidebar/styledComponent';
import { useIsMobileSize } from 'src/hooks/useIsMobile';
import { ConsultantButton } from '../buttons/ConsultantButton';

const MAX_ICON_WIDTH = 96;

const useTopContainerStyles = makeStyles((theme) => ({
    appBar: {
        borderBottomLeftRadius: theme.shape.borderRadius,
        position: 'fixed',
        display: 'flex',
        alignItems: 'flex-end',
        ...theme.mixins.toolbar,
        [theme.breakpoints.down('xs')]: {
            borderBottomLeftRadius: 0
        }
    }
}));

const useStyle = makeStyles((theme) =>
    createStyles({
        menuContainer: {
            width: '100vw',
            padding: `0 ${theme.spacing(3)}px`,
            justifyContent: 'space-between',
            // alignItems: 'flex-end',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            [theme.breakpoints.down('xs')]: {
                paddingTop: theme.spacing(3),
                
            }
        },
        active: {
            color: theme.palette.primary.main,
            borderBottom: `solid 1px ${theme.palette.primary.main}`
        },
        apsyIcon: {
            maxWidth: MAX_ICON_WIDTH,
            width: '10vmax',
            paddingTop: 4,
            margin: '0 16px'
        },
        iconWrapper: {
            position: 'absolute',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bottom: '-20%'
        },
        mobileHeadContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            [theme.breakpoints.down('xs')]: {
                // width: '100%'
            }
        }
    })
);

interface Props {
    // layoutdispatch: React.Dispatch<any>;
    sidebarType?: SidebarTypes | undefined;
    disableButtons?: boolean;
}
interface ContainerProps {
    navVariant: AppBackVariant;
}

export const TopNavContainer = React.memo(
    forwardRef(({ navVariant, children, ...rest }: ContainerProps & AppCommonChild, ref) => {
        const layoutClasses = baseLayoutStyles();
        const classes = useTopContainerStyles();
        const isMobileSize = useIsMobileSize();
        const state = useBaseLayoutState();
        return (
            <div
                className={clsx(classes.appBar, {
                    [layoutClasses.sideBackground]:
                        isMobileSize && (state.leftOpen || state.rightOpen),
                    [layoutClasses.darkBackground]: navVariant === 'dark',
                    [layoutClasses.monoliteBackground]: navVariant === 'monolite',
                    [layoutClasses.lightBackground]: navVariant === 'light'
                })}
                {...rest}
                ref={ref as any}>
                {children}
            </div>
        );
    })
);

const useDrawerStyles = makeStyles({ hide: { visibility: 'hidden' } });

const MobileDrawerButton = memo(({ disableButtons }: Props) => {
    const state = useBaseLayoutState();
    const onCloseSidebar = useBaseLayoutCloseSide();
    const onOpenSidebar = useBaseLayoutOpenSide();
    const classes = useDrawerStyles();
    return (
        <>
            {state.leftOpen || state.rightOpen ? (
                <CloseButton handleClose={onCloseSidebar} />
            ) : (
                <IconButton onClick={() => onOpenSidebar('SMALL_NAVBAR')}>
                    <MenuIcon mdIcon className={clsx({ [classes.hide]: disableButtons })} />
                </IconButton>
            )}
        </>
    );
});

export const TopNavigator = React.memo(({ disableButtons }: Props) => {
    const classes = useStyle();

    const { t } = useTranslation('common');
    // const { user, signOut } = useAuth();

    const onOpenSidebar = useBaseLayoutOpenSide();
    const state = useBaseLayoutState();
    const sidebarType = state.sidebarType;

    return (
        <div className={classes.menuContainer}>
            <>
                <Hidden smDown>
                    {!disableButtons && (
                        <>
                            <div>
                                <NavButton
                                    onClick={() => onOpenSidebar('APPMODELS')}
                                    variant="text"
                                    className={clsx({
                                        [classes.active]: sidebarType === 'APPMODELS'
                                    })}>
                                    <NavTypography>{t('nav.appModels')}</NavTypography>
                                </NavButton>

                                <NavButton
                                    onClick={() => onOpenSidebar('ABOUT')}
                                    variant="text"
                                    className={clsx({
                                        [classes.active]: sidebarType === 'ABOUT'
                                    })}>
                                    <NavTypography>{t('nav.about')}</NavTypography>
                                </NavButton>

                                <NavButton
                                    onClick={() => onOpenSidebar('PRICING')}
                                    variant="text"
                                    className={clsx({
                                        [classes.active]: sidebarType === 'PRICING'
                                    })}>
                                    <NavTypography>{t('nav.pricing')}</NavTypography>
                                </NavButton>
                                <Link href="/how-it-works">
                                    <a>
                                        <NavButton
                                            variant="text"
                                            className={clsx({ [classes.active]: false })}>
                                            <NavTypography>{t('nav.howWorks')}</NavTypography>
                                        </NavButton>
                                    </a>
                                </Link>
                            </div>
                            <div>
                                <ConsultantButton />
                                {/* {!!user ? (
                                    <NavButton onClick={() => signOut()} variant="text">
                                        <NavTypography>{t('nav.logout')}</NavTypography>
                                    </NavButton>
                                ) : (
                                    <NavButton
                                        onClick={() => onOpenSidebar('REGISTER', true)}
                                        variant="text"
                                        className={clsx({
                                            [classes.active]: sidebarType === 'LOGIN'
                                        })}>
                                        <NavTypography>{t('nav.login')}</NavTypography>
                                    </NavButton>
                                )} */}
                            </div>
                        </>
                    )}
                    <Link href="/">
                        <a className={classes.iconWrapper}>
                            <ApsyIcon className={classes.apsyIcon} />
                        </a>
                    </Link>
                </Hidden>
                <Hidden mdUp>
                    <div className={classes.mobileHeadContainer}>
                        <MobileDrawerButton disableButtons={disableButtons} />
                        <Link href="/">
                            <a>
                                <ApsyIcon className={classes.apsyIcon} />
                            </a>
                        </Link>
                    </div>
                    <Hidden mdUp>
                        <ConsultantButton />
                    </Hidden>
                </Hidden>
            </>
        </div>
    );
});
